import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Baby, CalendarDays, Users } from "lucide-react";
import { latnLocale } from "../../../../lib/format";
import { KitStepper } from "./stepper";
import {
  CHOSEN,
  CONTROL,
  KIT,
  PRIMARY,
  chatT,
  isoToUtcNoon,
  kitPanel,
  longDate,
  partyPhrase,
  shortDate,
} from "./kit-tokens";

export type DatePartyNeed = "date" | "adults" | "children";

/** Today's date in Morocco as YYYY-MM-DD, whatever the browser's zone. */
export function moroccoToday(now: Date = new Date()): string {
  try {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Africa/Casablanca",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** The next `count` days from Morocco's today, as YYYY-MM-DD. */
export function nextDays(count: number, now: Date = new Date()): string[] {
  const start = isoToUtcNoon(moroccoToday(now));
  return Array.from({ length: count }, (_, i) =>
    new Date(start.getTime() + i * 86_400_000).toISOString().slice(0, 10),
  );
}

function capitalize(s: string, lang: string): string {
  return s ? s.charAt(0).toLocaleUpperCase(lang) + s.slice(1) : s;
}

/** "Today" / "Tomorrow" from Intl, else the short weekday. */
function dayLabel(iso: string, index: number, lang: string): string {
  if (index < 2) {
    try {
      return capitalize(new Intl.RelativeTimeFormat(lang, { numeric: "auto" }).format(index, "day"), lang);
    } catch {
      /* fall through to the weekday */
    }
  }
  try {
    return new Intl.DateTimeFormat(latnLocale(lang), { weekday: "short", timeZone: "UTC" }).format(isoToUtcNoon(iso));
  } catch {
    return "";
  }
}

function dayNumber(iso: string, lang: string): string {
  try {
    return new Intl.DateTimeFormat(latnLocale(lang), { day: "numeric", timeZone: "UTC" }).format(isoToUtcNoon(iso));
  } catch {
    return iso.slice(8, 10);
  }
}

/**
 * C20: the assistant needs a day and a party before it can go on.
 *
 * The next few days in Morocco time with one navy highlight that slides
 * between them (a transform, never a layout move), an adults stepper, and
 * children only when the agent asked for them. "Use these" sends one natural
 * sentence in the chat's language and folds the card into a chip with
 * "Change".
 *
 * The days are computed after mount: the server is on UTC and the guest's
 * clock may not be, so rendering them on the server would hand React two
 * different strips (the hydration date-strip bug, React #418).
 */
export function DatePartyAsk({
  question,
  needs,
  lang,
  onSend,
  locked = false,
  dayCount = 6,
  initialAdults = 2,
}: {
  question?: string;
  needs: DatePartyNeed[];
  /** The chat's language: labels and the sent sentence follow it. */
  lang: string;
  onSend: (text: string) => void;
  locked?: boolean;
  /** How many days to offer, 5 to 7. */
  dayCount?: number;
  initialAdults?: number;
}) {
  const { t: baseT, i18n } = useTranslation();
  const t = chatT(i18n, baseT, lang);
  const rtl = lang === "ar";
  const askDate = needs.length === 0 || needs.includes("date");
  const askChildren = needs.includes("children");
  const askAdults = needs.length === 0 || needs.includes("adults") || askChildren;

  const count = Math.min(7, Math.max(5, dayCount));
  const [days, setDays] = useState<string[] | null>(null);
  useEffect(() => setDays(nextDays(count)), [count]);

  const [day, setDay] = useState(0);
  const [adults, setAdults] = useState(Math.max(1, initialAdults));
  const [children, setChildren] = useState(0);
  const [folded, setFolded] = useState(false);

  const chosenIso = days?.[day];
  const party = partyPhrase(t, adults, askChildren ? children : 0);

  const send = () => {
    if (askDate && !chosenIso) return;
    const date = chosenIso ? longDate(chosenIso, lang) : "";
    const text =
      askDate && askAdults
        ? t("ai_concierge.kit.when_who.send_both", { date, party })
        : askDate
          ? t("ai_concierge.kit.when_who.send_date", { date })
          : t("ai_concierge.kit.when_who.send_party", { party });
    setFolded(true);
    onSend(text);
  };

  if (folded) {
    const parts = [askDate && chosenIso ? shortDate(chosenIso, lang) : null, askAdults ? party : null].filter(Boolean);
    return (
      <span
        data-testid="kit-when-who-chip"
        className="inline-flex max-w-full items-center gap-2 self-start rounded-full py-1 pe-1 ps-3 text-[13px] font-semibold"
        style={{ ...CONTROL, color: KIT.navy }}
      >
        <CalendarDays size={14} aria-hidden className="shrink-0" />
        {/* The chosen date and party wrap rather than cut: they are the answer the guest gave. */}
        <span className="min-w-0 break-words">{parts.join(" · ")}</span>
        {!locked ? (
          <button
            type="button"
            onClick={() => setFolded(false)}
            className="min-h-[28px] shrink-0 rounded-full px-3 text-[12px]"
            style={{ background: KIT.softTint }}
          >
            {t("ai_concierge.kit.when_who.change")}
          </button>
        ) : null}
      </span>
    );
  }

  return (
    <div data-testid="kit-when-who" className="flex flex-col gap-3 p-3" style={kitPanel()}>
      <p className="text-[11px] font-bold uppercase tracking-[0.1em]" style={{ color: KIT.muted }}>
        {question || t("ai_concierge.kit.when_who.title")}
      </p>

      {askDate ? (
        <div
          role="radiogroup"
          aria-label={t("ai_concierge.kit.when_who.day")}
          className="relative grid"
          style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}
        >
          {days ? (
            <span
              aria-hidden
              className="absolute inset-y-0 rounded-[12px] transition-transform duration-[260ms] ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none"
              style={{
                ...CHOSEN,
                width: `${100 / count}%`,
                insetInlineStart: 0,
                transform: `translateX(${(rtl ? -1 : 1) * day * 100}%)`,
              }}
            />
          ) : null}
          {(days ?? Array.from({ length: count }, () => "")).map((iso, i) => {
            const on = !!iso && i === day;
            return (
              <button
                key={iso || i}
                type="button"
                role="radio"
                aria-checked={on}
                aria-label={iso ? longDate(iso, lang) : undefined}
                disabled={!iso || locked}
                onClick={() => setDay(i)}
                className="relative z-[1] flex min-h-[50px] min-w-0 flex-col items-center justify-center transition-colors duration-200"
                style={{ color: on ? "#fff" : KIT.navy }}
              >
                {/* "Aujourd'hui" is wider than a seventh of the strip: it breaks
                    (hyphenated where the browser can) instead of losing letters. */}
                <span
                  className="max-w-full hyphens-auto px-0.5 text-center text-[10.5px] leading-tight [overflow-wrap:anywhere]"
                  style={{ color: on ? "rgba(255,255,255,0.82)" : KIT.muted }}
                >
                  {iso ? dayLabel(iso, i, lang) : " "}
                </span>
                <span className="text-[15px] font-semibold tabular-nums">{iso ? dayNumber(iso, lang) : " "}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      {askAdults ? (
        <KitStepper
          label={t("ai_concierge.kit.when_who.adults")}
          value={adults}
          min={1}
          max={20}
          onChange={setAdults}
          lessLabel={t("ai_concierge.kit.when_who.fewer_adults")}
          moreLabel={t("ai_concierge.kit.when_who.more_adults")}
          icon={<Users size={14} aria-hidden />}
        />
      ) : null}
      {askChildren ? (
        <KitStepper
          label={t("ai_concierge.kit.when_who.children")}
          value={children}
          min={0}
          max={10}
          onChange={setChildren}
          lessLabel={t("ai_concierge.kit.when_who.fewer_children")}
          moreLabel={t("ai_concierge.kit.when_who.more_children")}
          icon={<Baby size={14} aria-hidden />}
        />
      ) : null}

      <button
        type="button"
        onClick={send}
        disabled={locked || (askDate && !chosenIso)}
        className="min-h-[44px] w-full rounded-full text-[13.5px] font-semibold disabled:opacity-50"
        style={PRIMARY}
      >
        {t("ai_concierge.kit.when_who.use")}
      </button>
    </div>
  );
}
